import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import SortingArrowSvg from '../../assets/icon-sort.svg';
import { usePrevious } from '../../utils/utils';

const Wrapper = styled.div``;

const NumberInput = styled.input`
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  appearance: textfield;
  text-align: right;
  line-height: normal;
  padding-top: 0px;
  padding-bottom: 0px;
  width: 100%;
  height: 34px;
  border: 1px solid ${({ theme }): string => theme.colors.black10Alpha};
  margin-right: 5px;
  padding-right: 30px;
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  &:focus {
    outline: none;
    border: 1px solid ${({ theme }): string => theme.colors.brandBlue};
  }
`;

const NumberInputWrapper = styled.div`
  display: flex;
  &:hover {
    ${NumberInput} {
      :not(:focus) {
        border: 1px solid
          ${({ theme }): string => theme.colors.brandBlue20Alpha};
      }
    }
  }
`;
const ButtonsOuterWrapper = styled.div`
  position: relative;
`;

const CounterButtonWrapper = styled.div`
  display: grid;
  position: absolute;
  right: 5px;
`;
const CounterButton = styled.button<{ bottomDivider?: true }>`
  background: transparent;
  border: 1px solid ${({ theme }): string => theme.colors.black10Alpha};
  border-top: none;
  border-right: none;
  height: 18px;
  border-bottom: ${({ bottomDivider, theme }): string =>
    bottomDivider ? `border: 1px solid ${theme.colors.black10Alpha}` : 'none;'};
  &:focus {
    outline: none;
  }
  &:hover {
    cursor: pointer;
  }
`;
const CounterIcon = styled.img<{ isDown?: boolean }>`
  width: 8px;
  color: ${({ theme }): string => theme.colors.brandBlue};
  ${({ isDown }): string => (isDown ? 'transform: rotate(180deg);' : '')};
`;

export interface NumberStepperProps {
  count?: number | null;
  min?: number;
  max?: number;
  placeholder?: string;
  onChange?: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  'data-testid'?: string;
}

export const NumberStepper: React.FC<NumberStepperProps> = ({
  count,
  min,
  max,
  placeholder,
  onBlur,
  onChange,
  'data-testid': dataTestId = 'NumberStepper',
}) => {
  const initCout = count || count === 0 ? count : null;
  const [value, setValue] = useState<number | null>(initCout);
  const prevValue = usePrevious<number | null>(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(count !== null && count !== undefined ? count : null);
  }, [count]);

  useEffect(() => {
    if (onChange && prevValue !== value && prevValue !== undefined) {
      onChange(value);
    }
  }, [value, prevValue, onChange]);

  return (
    <Wrapper data-testid={dataTestId}>
      <NumberInputWrapper>
        <NumberInput
          type="number"
          min={min}
          max={max}
          value={value || value === 0 ? value : ''}
          placeholder={placeholder}
          ref={inputRef}
          onKeyDown={(event) => {
            const prohibitedKeys = ['+', 'e', '.'];
            if (prohibitedKeys.includes(event.key)) {
              event.preventDefault();
              return;
            }
            if (event.key === '-') {
              const newValue = parseInt(`-${value || '1'}`, 10);
              if (
                (!min && min !== 0) ||
                ((min || min === 0) && newValue >= min)
              ) {
                setValue(newValue);
              }
              event.preventDefault();
            }
          }}
          onChange={(event) => {
            const newValue = parseInt(event.target.value, 10);
            if ((max || max === 0) && newValue > max) {
              setValue(max);
              event.preventDefault();
            } else if ((min || min === 0) && newValue < min) {
              setValue(min);
              event.preventDefault();
            } else {
              setValue(newValue);
            }
          }}
          onBlur={(event) => {
            if (onBlur) {
              onBlur(parseInt(event.target.value, 10));
            }
          }}
          onPaste={(event) => {
            event.preventDefault();
          }}
          data-testid={`${dataTestId}_Input`}
        />
        <ButtonsOuterWrapper>
          <CounterButtonWrapper>
            <CounterButton
              type="button"
              onClick={(): void => {
                if (!value && value !== 0 && (min || min === 0)) {
                  setValue(min);
                } else if ((!value && value !== 0) || Number.isNaN(value)) {
                  setValue(1);
                } else if (max === undefined || value < max) {
                  setValue(value + 1);
                }
              }}
              bottomDivider
              data-testid={`${dataTestId}_UpButton`}
            >
              <CounterIcon src={SortingArrowSvg} />
            </CounterButton>
            <CounterButton
              type="button"
              onClick={(): void => {
                const currentValue = value || 0;
                if (!value && value !== 0 && (min || min === 0)) {
                  setValue(min);
                } else if ((!value && value !== 0) || Number.isNaN(value)) {
                  setValue(max ? max - 1 : 0);
                } else if (min === undefined || currentValue > min) {
                  setValue(currentValue - 1);
                }
              }}
              data-testid={`${dataTestId}_DownButton`}
            >
              <CounterIcon src={SortingArrowSvg} isDown />
            </CounterButton>
          </CounterButtonWrapper>
        </ButtonsOuterWrapper>
      </NumberInputWrapper>
    </Wrapper>
  );
};

export default NumberStepper;
